// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
// <GetUserSnippet>
import {
  Client,
  GraphRequestOptions,
  PageCollection,
  PageIterator
} from '@microsoft/microsoft-graph-client'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import { Event, User } from '@microsoft/microsoft-graph-types'
import { getDefaultTimezone } from 'index'
import { Calendars } from 'types/ms_outlook'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

let graphClient: Client | undefined = undefined

function ensureClient(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  useBeta: boolean = false
) {
  // if (!graphClient) {
  graphClient = Client.initWithMiddleware({
    authProvider: authProvider,
    defaultVersion: useBeta ? 'beta' : 'v1.0'
  })
  // }

  return graphClient
}

export async function getUser(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider
): Promise<User> {
  ensureClient(authProvider)

  // Return the /me API endpoint result as a User object
  const user: User = await graphClient
    ?.api('/me')
    // Only retrieve the specific fields needed
    .select('displayName,mail,mailboxSettings,userPrincipalName')
    .get()

  return user
}
// </GetUserSnippet>

export async function getUserCalendars(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider
): Promise<any> {
  ensureClient(authProvider)

  // Return the /me API endpoint result as a User object
  const calendars: Calendars = await graphClient
    ?.api('/me/calendars')
    .select(
      'id,name,owner,color,changeKey,canShare,canViewPrivateItems,canEdit,hexColor,isDefaultCalendar'
    ) // Add any other fields you need
    .get()

  return calendars.value
}

// <GetUserWeekCalendarSnippet>
export async function getUserWeekCalendar(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  timeZone: string,
  calendarId: string | undefined = undefined,
  from: string,
  to: string,
  useBeta: boolean = false
): Promise<Event[]> {
  ensureClient(authProvider, useBeta)

  // getUserEvents already returns events with full recurrence details via batch fetching
  // No need to call enrichEventsWithRecurrenceDetails separately
  const parentEvents = await getUserEvents(
    authProvider,
    timeZone,
    calendarId,
    from,
    to,
    useBeta
  )

  // Collect all events including exception occurrences as separate events
  const allEvents: Event[] = []

  parentEvents.forEach((event) => {
    const rruleString = getICalRecurrenceRules(event)

    ;(event as any).rruleString = rruleString

    // Add the parent event
    allEvents.push(event)

    // Extract and add exception occurrences as separate events
    const exceptionOccurrences = (event as any).exceptionOccurrences as
      | Event[]
      | undefined
    if (exceptionOccurrences && exceptionOccurrences.length > 0) {
      exceptionOccurrences.forEach((exceptionEvent: Event) => {
        // Mark this as an exception occurrence so you can identify it later
        ;(exceptionEvent as any).isExceptionOccurrence = true
        ;(exceptionEvent as any).parentEventId = event.id
        allEvents.push(exceptionEvent)
      })
    }
  })

  return allEvents
}
// </GetUserWeekCalendarSnippet>

// <CreateEventSnippet>
export async function createEvent(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  newEvent: Event,
  calendarId: string
): Promise<Event> {
  ensureClient(authProvider)

  // POST /me/events
  // JSON representation of the new event is sent in the
  // request body
  return await graphClient
    ?.api(`/me/calendars/${calendarId}/events`)
    .post(newEvent)
    .then((createdEvent) => {
      return {
        ...createdEvent,
        rruleString: getICalRecurrenceRules(createdEvent)
      }
    })
}
// </CreateEventSnippet>

export async function getEventDetailsById(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  eventId: string,
  includeRecurrenceDetails: boolean = false,
  useBeta: boolean = false
): Promise<Event | undefined> {
  ensureClient(authProvider, useBeta)

  let request = graphClient
    ?.api(`/me/events/${eventId}`)
    .header('Prefer', `outlook.timezone="${getDefaultTimezone()}"`)

  // Use beta version if requested
  if (useBeta) {
    request = request?.version('beta')
  }

  // If we want recurrence details, select cancelledOccurrences and exceptionOccurrences
  if (includeRecurrenceDetails) {
    request = request?.select('*,cancelledOccurrences,exceptionOccurrences')
  }

  const event = await request?.get()
  event.rruleString = getICalRecurrenceRules(event)
  return event
}

/**
 * Gets all instances (occurrences) of a recurring event
 * @param authProvider - The authentication provider
 * @param seriesMasterId - The ID of the series master event
 * @param startDateTime - Start of date range (ISO format)
 * @param endDateTime - End of date range (ISO format)
 * @param timeZone - Timezone for the query
 * @returns Array of event instances
 */
export async function getEventInstances(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  seriesMasterId: string,
  startDateTime: string,
  endDateTime: string,
  timeZone?: string
): Promise<Event[]> {
  ensureClient(authProvider)

  const tz = timeZone || getDefaultTimezone()

  const response: PageCollection = await graphClient
    ?.api(`/me/events/${seriesMasterId}/instances`)
    .header('Prefer', `outlook.timezone="${tz}"`)
    .query({ startDateTime, endDateTime })
    .top(200)
    .get()

  if (response['@odata.nextLink']) {
    const instances: Event[] = []
    const options: GraphRequestOptions = {
      headers: { Prefer: `outlook.timezone="${tz}"` }
    }

    const pageIterator = new PageIterator(
      graphClient!,
      response,
      (event) => {
        instances.push(event)
        return true
      },
      options
    )

    await pageIterator.iterate()
    return instances
  } else {
    return response.value
  }
}

export async function updateEvent(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  eventId: string,
  updatedEvent: Event
): Promise<Event> {
  ensureClient(authProvider)
  return graphClient?.api(`/me/events/${eventId}`).update(updatedEvent)
}

export async function updateAttendeeStatus(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  status: string,
  eventId: string
): Promise<void> {
  try {
    ensureClient(authProvider)

    let type = 'accept'

    if (status === 'declined') {
      type = 'decline'
    } else if (status === 'tentativelyAccepted') {
      type = 'tentativelyAccept'
    }

    const result = await graphClient
      ?.api(`/me/calendar/events/${eventId}/${type}`)
      .post({ sendResponse: true })
    console.log(result)

    console.log('Event status successfully changed to ', status)
  } catch (error) {
    console.error('Error updating event', error)
    throw error
  }
}

export async function deleteEvent(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  eventId: string
): Promise<Event> {
  ensureClient(authProvider)
  try {
    const result = await graphClient?.api(`/me/events/${eventId}`).delete()
    console.log('Event updated successfully', result)
    return result
  } catch (error) {
    console.error('Error updating event', error)
    throw error
  }
}

/**
 * Gets user events using calendarview endpoint for the date range
 * For recurring event instances, batches and fetches parent series once to preserve recurrence details
 * @param authProvider - The authentication provider
 * @param timeZone - Timezone for the query
 * @param calendarId - Optional calendar ID (defaults to primary calendar)
 * @param from - Start date (ISO format)
 * @param to - End date (ISO format)
 * @param useBeta - Whether to use beta API endpoint
 * @returns Array of events with recurrence rules preserved
 */
export async function getUserEvents(
  authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  timeZone: string,
  calendarId: string | undefined = undefined,
  from?: string,
  to?: string,
  useBeta: boolean = false
): Promise<Event[]> {
  ensureClient(authProvider, useBeta)

  // If no date range provided, use regular events endpoint
  if (!from || !to) {
    let apiUrl = calendarId
      ? `/me/calendars/${calendarId}/events`
      : '/me/events'
    let request = graphClient
      ?.api(apiUrl)
      .header('Prefer', `outlook.timezone="${timeZone}"`)
      .select('*,cancelledOccurrences,exceptionOccurrences')

    if (useBeta) {
      request = request?.version('beta')
    }

    const response: PageCollection = await request
      ?.orderby('start/dateTime')
      .top(200)
      .get()

    if (response['@odata.nextLink']) {
      const events: Event[] = []
      const options: GraphRequestOptions = {
        headers: { Prefer: `outlook.timezone="${timeZone}"` }
      }

      const pageIterator = new PageIterator(
        graphClient!,
        response,
        (event) => {
          events.push(event)
          return true
        },
        options
      )

      await pageIterator.iterate()
      return events
    } else {
      return response.value
    }
  }

  // Use calendarview for date range queries
  const apiUrl = calendarId
    ? `/me/calendars/${calendarId}/calendarview`
    : '/me/calendarview'

  let request = graphClient
    ?.api(apiUrl)
    .header('Prefer', `outlook.timezone="${timeZone}"`)
    .query({ startDateTime: from, endDateTime: to })

  if (useBeta) {
    request = request?.version('beta')
  }

  const response: PageCollection = await request
    ?.orderby('start/dateTime')
    .top(200)
    .get()

  let calendarViewEvents: Event[] = []

  if (response['@odata.nextLink']) {
    const options: GraphRequestOptions = {
      headers: { Prefer: `outlook.timezone="${timeZone}"` }
    }

    const pageIterator = new PageIterator(
      graphClient!,
      response,
      (event) => {
        calendarViewEvents.push(event)
        return true
      },
      options
    )

    await pageIterator.iterate()
  } else {
    calendarViewEvents = response.value
  }

  // Accumulate unique series IDs from recurring event instances
  const seriesIds = new Set<string>()
  const nonRecurringEvents: Event[] = []

  for (const event of calendarViewEvents) {
    if (event.type === 'occurrence' && (event as any).seriesMasterId) {
      // Accumulate series IDs to fetch later
      seriesIds.add((event as any).seriesMasterId)
    } else if (event.type === 'seriesMaster') {
      // Accumulate series master IDs
      seriesIds.add(event.id!)
    } else {
      // Non-recurring events - keep as-is
      nonRecurringEvents.push(event)
    }
  }

  // Batch fetch all parent series events using Graph $batch endpoint
  const parentSeriesEvents: Event[] = []

  if (seriesIds.size > 0) {
    const seriesIdArray = Array.from(seriesIds)

    // Process in batches of 20 (Microsoft Graph recommends max 20 per batch)
    const BATCH_SIZE = 20
    for (let i = 0; i < seriesIdArray.length; i += BATCH_SIZE) {
      const batchIds = seriesIdArray.slice(i, i + BATCH_SIZE)

      // Create batch request body with timezone header
      const batchRequests = batchIds.map((id, index) => ({
        id: String(index),
        method: 'GET',
        url: `/me/events/${id}?$select=*,cancelledOccurrences,exceptionOccurrences`,
        headers: {
          Prefer: `outlook.timezone="${timeZone}"`
        }
      }))

      try {
        const batchResponse = await graphClient
          ?.api('$batch')
          .post({ requests: batchRequests })

        // Process batch responses
        if (batchResponse && batchResponse.responses) {
          for (const response of batchResponse.responses) {
            if (response.status === 200 && response.body) {
              const event = response.body as Event
              ;(event as any).rruleString = getICalRecurrenceRules(event)
              parentSeriesEvents.push(event)
            } else {
              console.warn(
                `Failed to fetch series event in batch, status: ${response.status}`
              )
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch series events batch:', error)
        // Fall back to individual fetches for this batch
        for (const seriesMasterId of batchIds) {
          try {
            const seriesEvent = await getEventDetailsById(
              authProvider,
              seriesMasterId,
              true,
              useBeta
            )
            if (seriesEvent) {
              parentSeriesEvents.push(seriesEvent)
            }
          } catch (err) {
            console.warn(`Failed to fetch series event ${seriesMasterId}:`, err)
          }
        }
      }
    }
  }

  // Combine all events: non-recurring + fetched parent series
  return [...nonRecurringEvents, ...parentSeriesEvents]
}

/**
 * Converts Microsoft Graph Event recurrence pattern to iCal RRULE format
 * @param event - The Microsoft Graph Event object
 * @returns Object containing RRULE string and EXDATE array, or null if no recurrence
 */
export function convertOutlookRecurrenceToRRule(event: Event): {
  rrule: string
  exdates: string[]
} | null {
  if (
    !event.recurrence ||
    !event.recurrence.pattern ||
    !event.recurrence.range
  ) {
    return null
  }

  const pattern = event.recurrence.pattern
  const range = event.recurrence.range
  const rruleParts: string[] = ['RRULE:FREQ=']

  // 1. Determine frequency
  switch (pattern.type) {
    case 'daily':
      rruleParts.push('DAILY')
      break
    case 'weekly':
    case 'relativeMonthly':
    case 'relativeYearly':
      rruleParts.push('WEEKLY')
      break
    case 'absoluteMonthly':
      rruleParts.push('MONTHLY')
      break
    case 'absoluteYearly':
      rruleParts.push('YEARLY')
      break
    default:
      rruleParts.push('DAILY')
  }

  // 2. Add interval
  if (pattern.interval && pattern.interval > 1) {
    rruleParts.push(`;INTERVAL=${pattern.interval}`)
  }

  // 3. Add BYDAY for weekly patterns
  if (pattern.daysOfWeek && pattern.daysOfWeek.length > 0) {
    const days = pattern.daysOfWeek
      .map((day) => {
        const dayMap: Record<string, string> = {
          sunday: 'SU',
          monday: 'MO',
          tuesday: 'TU',
          wednesday: 'WE',
          thursday: 'TH',
          friday: 'FR',
          saturday: 'SA'
        }
        return dayMap[day.toLowerCase()]
      })
      .filter(Boolean)
      .join(',')

    if (days) {
      rruleParts.push(`;BYDAY=${days}`)
    }
  }

  // 4. Add BYMONTHDAY for absolute monthly patterns
  if (pattern.type === 'absoluteMonthly' && pattern.dayOfMonth) {
    rruleParts.push(`;BYMONTHDAY=${pattern.dayOfMonth}`)
  }

  // 5. Add BYSETPOS for relative patterns (e.g., "first Monday")
  if (
    (pattern.type === 'relativeMonthly' || pattern.type === 'relativeYearly') &&
    pattern.index
  ) {
    const indexMap: Record<string, number> = {
      first: 1,
      second: 2,
      third: 3,
      fourth: 4,
      last: -1
    }
    const setPos = indexMap[pattern.index.toLowerCase()]
    if (setPos) {
      rruleParts.push(`;BYSETPOS=${setPos}`)
    }
  }

  // 6. Add BYMONTH for yearly patterns
  if (
    (pattern.type === 'absoluteYearly' || pattern.type === 'relativeYearly') &&
    pattern.month
  ) {
    rruleParts.push(`;BYMONTH=${pattern.month}`)
  }

  // 7. Add WKST (week start day)
  if (pattern.firstDayOfWeek) {
    const dayMap: Record<string, string> = {
      sunday: 'SU',
      monday: 'MO',
      tuesday: 'TU',
      wednesday: 'WE',
      thursday: 'TH',
      friday: 'FR',
      saturday: 'SA'
    }
    const wkst = dayMap[pattern.firstDayOfWeek.toLowerCase()]
    if (wkst) {
      rruleParts.push(`;WKST=${wkst}`)
    }
  }

  // 8. Add recurrence range (end date or count)
  if (range.type === 'endDate' && range.endDate) {
    // Format: YYYYMMDD
    const endDate = new Date(range.endDate)
    const formattedDate = endDate.toISOString().split('T')[0].replace(/-/g, '')
    rruleParts.push(`;UNTIL=${formattedDate}`)
  } else if (range.type === 'numbered' && range.numberOfOccurrences) {
    rruleParts.push(`;COUNT=${range.numberOfOccurrences}`)
  }
  // If range.type === 'noEnd', no UNTIL or COUNT is added

  const rrule = rruleParts.join('')

  // 9. Convert cancelledOccurrences to EXDATE format
  const exdates: string[] = []
  let cancelledOccurrences = (event as any).cancelledOccurrences as
    | string[]
    | undefined
  const exceptionOccurrences = (event as any).exceptionOccurrences as
    | Event[]
    | undefined

  // populate exceptions from exceptionOccurrences to the cancelledOccurences
  if (exceptionOccurrences && exceptionOccurrences.length > 0) {
    if (!cancelledOccurrences) {
      cancelledOccurrences = []
    }

    exceptionOccurrences.forEach((exceptionEvent: Event) => {
      if (
        (exceptionEvent as any).occurrenceId &&
        (exceptionEvent as any).occurrenceId !== null
      ) {
        // Format: OID.{seriesMasterId}.{occurrence-start-date}
        const occurrenceId = (exceptionEvent as any).occurrenceId
        cancelledOccurrences!.push(occurrenceId)
      }
    })
  }

  if (cancelledOccurrences && cancelledOccurrences.length > 0) {
    // Get the event's timezone and start time
    const eventTimezone =
      event.start?.timeZone ||
      event.originalStartTimeZone ||
      getDefaultTimezone()
    const eventStartDate = event.start?.dateTime
      ? dayjs(event.start.dateTime).tz(eventTimezone)
      : null

    if (!eventStartDate) {
      console.warn('Could not determine event start time for exclusions')
      return { rrule, exdates }
    }

    // Extract time components in the event's timezone
    const startTimeHours = eventStartDate.hour()
    const startTimeMinutes = eventStartDate.minute()
    const startTimeSeconds = eventStartDate.second()

    cancelledOccurrences.forEach((occurrenceId: string) => {
      // Format: OID.{seriesMasterId}.{occurrence-start-date}
      // Extract the date part (last segment after the last dot)
      const parts = occurrenceId.split('.')
      if (parts.length >= 3) {
        const dateStr = parts[parts.length - 1]
        // dateStr format might be like "2025-10-23" or ISO format
        try {
          // Parse the date and set it in the event's timezone with the event's start time
          const exclusionDate = dayjs(dateStr)
            .tz(eventTimezone)
            .hour(startTimeHours)
            .minute(startTimeMinutes)
            .second(startTimeSeconds)
            .millisecond(0)

          // Format as YYYYMMDDTHHMMSS in the event's local timezone (no Z suffix)
          const formatted = exclusionDate.format('YYYYMMDDTHHmmss')
          exdates.push(formatted)
        } catch (e) {
          console.warn('Could not parse cancelled occurrence date:', dateStr)
        }
      }
    })
  }

  return {
    rrule,
    exdates
  }
}

/**
 * Formats RRULE and EXDATE in complete iCal format
 * @param event - The Microsoft Graph Event object
 * @returns Complete iCal recurrence rules string or empty string
 *
 * @example
 * const events = await getUserEvents(authProvider, 'America/New_York')
 * events.forEach(event => {
 *   const rruleString = getICalRecurrenceRules(event)
 *   if (rruleString) {
 *     console.log(rruleString)
 *     // Output example:
 *     // RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE,FR;COUNT=10
 *     // EXDATE:20251023T140000Z,20251030T140000Z
 *   }
 * })
 */
export function getICalRecurrenceRules(event: Event): string {
  const result = convertOutlookRecurrenceToRRule(event)
  if (!result) {
    return ''
  }

  const lines: string[] = [result.rrule]

  // Add EXDATE lines if there are any exclusions
  if (result.exdates.length > 0) {
    // Add each exclusion date on its own EXDATE line
    result.exdates.forEach((exdate) => {
      lines.push(`EXDATE:${exdate}`)
    })
  }

  return lines.join('\n')
}
